/**
 * Client-side impersonation state — D-074 (2026-05-22)
 *
 * Replaces the old localStorage "aip:viewAs" role string that only changed the
 * route. This module tracks a real impersonation target_user_id that the api()
 * helper sends as an X-Impersonate-User-Id header, so every server response
 * reflects the target's data (queue, plan, cadence, accounts).
 *
 * State model:
 *   - The REAL authenticated user is always in session.ts _cached.
 *   - When impersonating, the target's identity is held HERE in _target.
 *   - "Effective user" = _target ?? real (used by sidebar, role gates, etc).
 *   - The target_user_id is persisted to localStorage so refreshes survive.
 *   - On app load, /api/admin/impersonate/current verifies the server still
 *     has an active row. If not, the localStorage value is cleared.
 *
 * Components subscribe via subscribe() to re-render when state changes.
 */

const STORAGE_KEY = "aip:impersonate-target";

export interface ImpersonationTarget {
  id: string;
  email: string;
  name: string;
  role: "manager" | "bdr" | "ae" | "executive" | "marketing" | "se";
  tenantId: number;
  expiresAt: string | null;
  sessionId: string;
}

let _target: ImpersonationTarget | null = null;
const _listeners = new Set<() => void>();

function notify() {
  _listeners.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

export function getImpersonationTarget(): ImpersonationTarget | null {
  return _target;
}

export function getImpersonationTargetId(): string | null {
  return _target?.id ?? null;
}

export function setImpersonationTarget(t: ImpersonationTarget | null) {
  _target = t;
  try {
    if (t) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore — quota or private mode
  }
  notify();
}

export function clearImpersonationTarget() {
  setImpersonationTarget(null);
}

export function subscribe(fn: () => void): () => void {
  _listeners.add(fn);
  return () => { _listeners.delete(fn); };
}

/**
 * Restore the impersonation target from localStorage on app load. The actual
 * "is this still active on the server" check happens later via
 * verifyImpersonationWithServer() — but we read the cached value first so the
 * banner / sidebar render correctly during the initial paint instead of
 * flashing the real user, then the target.
 */
/**
 * clearScopedSession — Wave I (B-BDR6)
 *
 * Wipes every role-scoped localStorage key so that stale data from the
 * previous identity does not leak into the next impersonation context.
 * Safe to call before window.location.reload(); the keys will be
 * repopulated from the server on the next mount.
 *
 * Keys cleared:
 *   aip:cadence:*          — BDR cadence queue, programs, prospects
 *   aip:activity-log:*     — BDR / AE activity-log records + index keys
 *   aip:account-plan:*     — per-account plan state
 *   aip:helix:*            — helix composite answers
 *   aip:pipeline-dna:*     — pipeline DNA score cache
 *   aip:overrides:*        — score-override log
 *   aip:override-policy:*  — override policy setting
 *   aip:rapid-intake:*     — rapid intake draft
 *   aip:wizard-draft*      — wizard answer drafts
 *   aip:cp10-timing:*      — CP10 timing store
 *   aip:bdr-override-log   — BDR override log
 *   aip:viewAs             — legacy role-view string
 *   mi6.aip.accounts.*     — cached accounts list
 */
export function clearScopedSession(): void {
  const SCOPED_PREFIXES = [
    "aip:cadence:",
    "aip:activity-log:",
    "aip:account-plan:",
    "aip:helix:",
    "aip:pipeline-dna:",
    "aip:overrides:",
    "aip:override-policy:",
    "aip:rapid-intake:",
    "aip:wizard-draft",
    "aip:cp10-timing:",
    "aip:bdr-override-log",
    "aip:viewAs",
    "mi6.aip.accounts.",
  ];
  try {
    const keysToWipe = Object.keys(localStorage).filter((k) =>
      SCOPED_PREFIXES.some((prefix) => k.startsWith(prefix))
    );
    keysToWipe.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore — private mode or quota
  }
}

export function restoreImpersonationTargetFromStorage(): ImpersonationTarget | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.id === "string" && /^[0-9a-f-]{36}$/i.test(parsed.id)) {
      _target = parsed as ImpersonationTarget;
      return _target;
    }
  } catch {
    // ignore corrupt JSON
  }
  return null;
}
